import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Undo2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiClientError, apiClient } from '@/api/client'
import { isValidManagerPin, sanitizeManagerPin } from '@/lib/houseAccounts'
import { VOID_OUTCOME_REASONS } from '@/lib/void-reasons'
import { cn } from '@/lib/utils'
import type { UndoStaffMealResult } from '@/types'

/**
 * F-MM-N30 — a staff meal is not a sale, so it never gets a credit note.
 * Undoing one gives the whole meal back to the allowance, cancels the order
 * and writes it to the Void Log. If the food was made, the stock stays used
 * and is recorded as waste; if not, it goes back.
 */
export interface UndoStaffMealDialogProps {
  order: { id: string; order_number: string; total_amount: number; customer_name?: string | null }
  formatCurrency: (n: number) => string
  onClose: () => void
  onSuccess: (result: UndoStaffMealResult) => void
}

const WASTE_REASONS = VOID_OUTCOME_REASONS.wasted

export function UndoStaffMealDialog({ order, formatCurrency, onClose, onSuccess }: UndoStaffMealDialogProps) {
  const [reason, setReason] = useState('')
  const [foodMade, setFoodMade] = useState<boolean | null>(null)
  const [wasteCode, setWasteCode] = useState('')
  const [pin, setPin] = useState('')
  const [wrongPin, setWrongPin] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setReason('')
    setFoodMade(null)
    setWasteCode('')
    setPin('')
    setWrongPin(false)
    setError(null)
  }, [order.id])

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.undoStaffMeal(order.id, {
        manager_pin: pin,
        reason: reason.trim(),
        food_made: foodMade === true,
        reason_code: foodMade && wasteCode ? wasteCode : undefined,
      })
      if (!res.success || !res.data) throw new Error(res.message || 'Could not undo the staff meal')
      return res.data
    },
    onSuccess: (result) => onSuccess(result),
    onError: (err: Error) => {
      if (err instanceof ApiClientError && err.code === 'invalid_manager_pin') {
        setPin('')
        setWrongPin(true)
        setError(null)
        return
      }
      setError(err.message || 'Could not undo the staff meal')
    },
  })

  const canSubmit = reason.trim().length > 0 && foodMade !== null && isValidManagerPin(pin) && !mutation.isPending

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" aria-hidden />
            Undo staff meal
          </DialogTitle>
          <DialogDescription>
            Order <span className="font-semibold">{order.order_number}</span>
            {order.customer_name ? ` · ${order.customer_name}` : ''} · {formatCurrency(order.total_amount)}. The whole
            meal goes back on the allowance and the order is cancelled. It shows in the Void Log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="undo-meal-reason">Reason (required)</Label>
            <Input
              id="undo-meal-reason"
              placeholder="e.g. Charged to the wrong person"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">Was the food made?</span>
            <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Was the food made?">
              {(
                [
                  [true, 'Yes — it was made'],
                  [false, 'No — not made'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={String(v)}
                  type="button"
                  role="radio"
                  aria-checked={foodMade === v}
                  onClick={() => setFoodMade(v)}
                  className={cn(
                    'h-10 rounded-lg border text-sm font-medium transition-colors',
                    foodMade === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted/40',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {foodMade === true
                ? 'The stock stays used and is recorded as waste.'
                : foodMade === false
                  ? 'The stock goes back.'
                  : 'Decides whether the stock goes back or is recorded as waste.'}
            </p>
          </div>

          {foodMade ? (
            <div className="space-y-1.5">
              <Label htmlFor="undo-meal-waste">Why was it wasted? (optional)</Label>
              <select
                id="undo-meal-waste"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={wasteCode}
                onChange={(e) => setWasteCode(e.target.value)}
              >
                <option value="">—</option>
                {WASTE_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="undo-meal-pin">Manager PIN (4 digits)</Label>
            <Input
              id="undo-meal-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              placeholder="••••"
              value={pin}
              aria-invalid={wrongPin || undefined}
              onChange={(e) => {
                setPin(sanitizeManagerPin(e.target.value))
                setWrongPin(false)
              }}
              className="h-10 font-mono tracking-[0.5em]"
            />
            {wrongPin ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                Wrong PIN
              </p>
            ) : null}
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={!canSubmit} onClick={() => mutation.mutate()}>
            {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            Undo staff meal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
